import { memo, useCallback, useState } from 'react'
import { CSSObjectWithLabel } from 'react-select'
import { query, entity, like, string, some, ascending, notEquals, notNull } from '~/prix'
import unaccent from '~/prix/functions/unaccent'
import SelectFromQuery from '~/prix/react/components/form/inputs/selectFromQuery'

interface SelectAgentFilterProps {
  onSelectAgent: (agent: { agentCpf: string; name: string } | null) => void
  placeholder?: string
  value?: { agentCpf: string; name: string } | null
  withServiceAreaOnly?: boolean
}

const SelectAgentFilter = ({
  onSelectAgent,
  placeholder = 'Selecione um agente',
  value,
  withServiceAreaOnly = false,
}: SelectAgentFilterProps) => {
  const [isDisabled, setIsDisabled] = useState(false)

  const attendantListQueryFactory = useCallback(
    (input: string) =>
      query('legalEntityAttendanceAttendant')
        .join({
          current: entity('legalEntityAttendanceAttendant').property('cpf'),
          target: entity('attendant').property('cpf'),
          join: 'inner',
        })
        .select({
          hash: entity('legalEntityAttendanceAttendant').property('hash'),
          agentCpf: entity('legalEntityAttendanceAttendant').property('cpf'),
          name: entity('legalEntityAttendanceAttendant').property('name'),
        })
        .where(
          ...[
            some(
              like(
                unaccent(entity('legalEntityAttendanceAttendant').property('name')),
                unaccent(string().value(`%${input.replace(/\s/g, '%')}%`)),
              ),
              like(
                entity('legalEntityAttendanceAttendant').property('cpf'),
                unaccent(string().value(`%${input.replace(/\s/g, '%')}%`)),
              ),
            ),
            notEquals(
              entity('legalEntityAttendanceAttendant').property('cpf'),
              string().value('-'),
            ),
            ...(withServiceAreaOnly ? [notNull(entity('attendant').property('serviceArea'))] : []),
          ],
        )
        .order(ascending('name'))
        .limit(20),
    [withServiceAreaOnly],
  )

  const handleChange = useCallback(
    (selectedAgent: any) => {
      if (selectedAgent?.agentCpf && selectedAgent?.name) {
        onSelectAgent({
          agentCpf: selectedAgent.agentCpf as string,
          name: selectedAgent.name as string,
        })

        setIsDisabled(true)
      }
    },
    [onSelectAgent],
  )

  return (
    <SelectFromQuery
      queryFactory={attendantListQueryFactory}
      idProperty='agentCpf'
      labelProperty='name'
      placeholder={placeholder}
      value={value ?? undefined}
      onDirectChange={handleChange}
      isDisabled={isDisabled}
      isClearable={false}
      styles={{
        control: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          width: '150px',
          minHeight: '32px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          '@media (max-width: 768px)': {
            width: '100%',
          },
        }),
        valueContainer: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '30px',
          padding: '0 6px',
        }),
        input: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        placeholder: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        singleValue: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        option: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        menuList: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '150px',
          overflowX: 'hidden',
        }),
      }}
    />
  )
}

export default memo(SelectAgentFilter)
